import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Card } from '@/entities/board';
import { useParticipants, useSetAssignment } from '@/entities/collab';
import { FormError } from '@/shared/forms';
import { Avatar, AvatarStack, Button, Popover } from '@/shared/ui';

import { useCardAbilities } from './abilities';
import styles from './Collab.module.css';

type Mode = 'unassigned' | 'shared' | 'assigned';

/**
 * Executors of the task: nobody (unassigned), explicitly shared with everyone who can see it,
 * or one or more people. Unassigned and shared are different states on purpose.
 */
export function AssigneeEditor({ card, boardId }: { card: Card; boardId: string }) {
  const { t } = useTranslation();
  const { assign } = useCardAbilities();
  const participants = useParticipants(boardId);
  const save = useSetAssignment(boardId);
  const [mode, setMode] = useState<Mode>(card.assignment_kind);
  const [selected, setSelected] = useState<string[]>(card.assignee_ids);
  const people = participants.data ?? [];
  const nameOf = (id: string) => people.find((p) => p.user_id === id)?.display_name ?? '…';
  const current = card.assignee_ids.map((id) => ({ id, name: nameOf(id) }));

  const summary =
    card.assignment_kind === 'shared' ? (
      <span className={styles.summaryText}>{t('assignment.shared')}</span>
    ) : card.assignment_kind === 'assigned' && current.length > 0 ? (
      <>
        <AvatarStack people={current} size="sm" max={4} />
        <span className={styles.summaryText}>{current.map((p) => p.name).join(', ')}</span>
      </>
    ) : (
      <span className={`${styles.summaryText} ${styles.muted}`}>{t('assignment.unassigned')}</span>
    );

  if (!assign) {
    return (
      <div className={styles.row}>
        <h4 className={styles.rowTitle}>{t('assignment.title')}</h4>
        <div className={styles.summaryButton} data-testid="assignment-summary">
          {summary}
        </div>
      </div>
    );
  }

  const apply = (close: () => void) => {
    const kind: Mode = mode === 'assigned' && selected.length === 0 ? 'unassigned' : mode;
    save.mutate(
      {
        cardId: card.id,
        kind,
        assigneeIds: kind === 'assigned' ? selected : [],
        expectedVersion: card.version,
      },
      { onSuccess: close },
    );
  };

  return (
    <div className={styles.row}>
      <h4 className={styles.rowTitle}>{t('assignment.title')}</h4>
      <Popover
        label={t('assignment.title')}
        width={320}
        trigger={(props) => (
          <button
            type="button"
            className={styles.summaryButton}
            data-testid="assignment-button"
            {...props}
            onClick={() => {
              setMode(card.assignment_kind);
              setSelected(card.assignee_ids);
              props.onClick();
            }}
          >
            {summary}
          </button>
        )}
      >
        {(close) => (
          <div className={styles.panel}>
            <fieldset className={styles.modes}>
              <legend>{t('assignment.mode')}</legend>
              {(['unassigned', 'shared', 'assigned'] as const).map((value) => (
                <label key={value} className={styles.option}>
                  <input
                    type="radio"
                    name={`assignment-${card.id}`}
                    checked={mode === value}
                    onChange={() => {
                      setMode(value);
                    }}
                  />
                  <span>{t(`assignment.mode_${value}`)}</span>
                </label>
              ))}
            </fieldset>
            {mode === 'assigned' && (
              <ul className={styles.people} aria-label={t('assignment.people')}>
                {people.map((person) => (
                  <li key={person.user_id}>
                    <label className={styles.person}>
                      <input
                        type="checkbox"
                        checked={selected.includes(person.user_id)}
                        onChange={(event) => {
                          setSelected((current) =>
                            event.target.checked
                              ? [...current, person.user_id]
                              : current.filter((id) => id !== person.user_id),
                          );
                        }}
                      />
                      <Avatar id={person.user_id} name={person.display_name} size="sm" />
                      <span>{person.display_name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <FormError error={save.error} />
            <div className={styles.actions}>
              <Button size="sm" onClick={close}>
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={save.isPending}
                onClick={() => {
                  apply(close);
                }}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
}
